import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import CommonLayout from "containers/Client/AccountPage/CommonLayout";
import { AuthContext } from "context/appContext";
import { ProfilClient } from "data/types";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import useIsMobile from "hooks/isMobile";
import { FC, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaFileInvoice } from "react-icons/fa";
import { fetchUserInvoices } from "redux/features/reservations/reservationSlice";
import Input from "shared/Input/Input";
import FactureDownloader from "./FactureDownloader";
import RecuDownloader from "./RecuDownloader";

export interface InvoicesPageProps {
  className?: string;
}

interface ModalState {
  loading: boolean;
}

const Invoices: FC<InvoicesPageProps> = ({ className = "" }) => {
  const authContext = useContext(AuthContext);
  const profile = authContext.user?.profil as ProfilClient;
  const dispatch = useAppDispatch();
  const { invoices, totalPages, currentPage, loading } = useAppSelector((state) => ({
    invoices: state.featureReservation.allInvoices?.invoices || [],
    totalPages: state.featureReservation.allInvoices?.totalPages || 0,
    currentPage: state.featureReservation.allInvoices?.currentPage || 1,
    loading: state.featureReservation.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState] = useState<ModalState>({ loading: false });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchData = async () => {
      if (profile) {
        setIsSearchLoading(true);
        await dispatch(fetchUserInvoices({ page, searchTerm: debouncedSearchTerm }));
        setIsSearchLoading(false);
      }
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm, profile]);
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleTableAction = (action: string, item: any) => {};

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (item: any) => (
        <img
          src={`${process.env.REACT_APP_CLOUD_FRONT}${item?.Location?.images[0]}`}
          alt={item.Location?.title}
          className="rounded-md w-14 h-14"
        />
      ),
    },
    {
      key: "espace",
      label: "Espace",
      render: (item: any) => <span className="align-middle">{item.Location?.title}</span>,
    },
    {
      key: "code",
      label: "Réference",
      render: (item: any) => <span className="align-middle">{item.reference}</span>,
    },
    {
      key: "recu",
      label: "Télécharger Récu",
      render: (item: any) => <RecuDownloader reservation={item} />,
    },
    {
      key: "facture",
      label: "Télécharger Facture",
      render: (item: any) => <FactureDownloader reservation={item} />,
    },
  ];

  return (
    <div
      className={`nc-AccountPage min-h-screen bg-gray-50 dark:bg-gray-900 ${className}`}
      data-nc-id="AccountPage"
    >
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <CommonLayout>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <FaFileInvoice className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste des Factures et Réçus
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par espace ou code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search invoices"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>
          {isSearchLoading && <Loading />}
          {loading === "pending" && !isSearchLoading && <Loading />}
          {loading !== "pending" && (
            <>
              {isMobile ? (
                <div className="space-y-4">
                  {invoices.map((item: any, index: number) => (
                    <div className="flex justify-center" key={index}>
                      <div className="w-full max-w-sm bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex flex-col items-center p-6">
                          <img
                            src={`${process.env.REACT_APP_CLOUD_FRONT}${item?.Location?.images[0]}`}
                            alt={item.Location?.title}
                            className="mb-4 rounded-md w-14 h-14"
                          />
                          <h5 className="mb-2 text-xl font-medium text-gray-900 dark:text-white">
                            {item.reference}
                          </h5>
                          <h5 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                            {item.Location?.title}
                          </h5>
                          <div className="flex space-x-4">
                            <RecuDownloader reservation={item} />
                            <FactureDownloader reservation={item} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <DynamicTable
                  columns={columns}
                  data={invoices}
                  totalItems={invoices.length}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onAction={handleTableAction}
                  totalPages={totalPages}
                  availableActions={[]}
                />
              )}
            </>
          )}
        </div>
      </CommonLayout>
    </div>
  );
};

export default Invoices;
